import React, { useCallback, useContext, useMemo } from 'react';
import styled from 'styled-components';
import Box from '@storeblocks/box';
import { SystemIcon } from '@storeblocks/icon';
import { DateTime } from 'luxon';
import CalendarContext from './CalendarContext';
import Square from './Square';
import { getYearRange } from './Views/Year';
function Header() {
    const { date, setDate, view, setView, max, min, locale } = useContext(CalendarContext);
    const back = useCallback(() => {
        if (view === 'day') {
            return setDate(date.minus({ month: 1 }));
        }
        if (view === 'month') {
            return setDate(date.minus({ years: 1 }));
        }
        return setDate(date.minus({ years: 10 }));
    }, [view, date, setDate]);
    const forward = useCallback(() => {
        if (view === 'day') {
            return setDate(date.plus({ month: 1 }));
        }
        if (view === 'month') {
            return setDate(date.plus({ years: 1 }));
        }
        return setDate(date.plus({ years: 10 }));
    }, [view, date, setDate]);
    const disabledForward = useMemo(() => {
        if (view === 'day') {
            return (date.plus({ month: 1 }).startOf('month') > DateTime.fromJSDate(max));
        }
        if (view === 'month') {
            return date.plus({ years: 1 }).startOf('year') > DateTime.fromJSDate(max);
        }
        return (getYearRange(date.plus({ years: 10 }).startOf('year')).start >
            DateTime.fromJSDate(max).year);
    }, [date, max, view]);
    const disabledBack = useMemo(() => {
        if (view === 'day') {
            return date.minus({ month: 1 }).endOf('month') < DateTime.fromJSDate(min);
        }
        if (view === 'month') {
            return date.minus({ years: 1 }).endOf('year') < DateTime.fromJSDate(min);
        }
        return (getYearRange(date.minus({ years: 10 }).endOf('year')).end <
            DateTime.fromJSDate(min).year);
    }, [date, min, view]);
    const arrows = useMemo(() => {
        let arrowsLabel;
        switch (view) {
            case 'day':
                arrowsLabel = 'month';
                break;
            case 'month':
                arrowsLabel = 'year';
                break;
            case 'year':
            default:
                arrowsLabel = 'year';
        }
        return (React.createElement(React.Fragment, null,
            React.createElement(Square, { px: "16px", onClick: back, disabled: disabledBack, "aria-label": `previous ${arrowsLabel}` },
                React.createElement(SystemIcon, { name: "chevron-left" })),
            React.createElement(Square, { px: "16px", onClick: forward, disabled: disabledForward, "aria-label": `next ${arrowsLabel}` },
                React.createElement(SystemIcon, { name: "chevron-right" }))));
    }, [back, forward, disabledForward, disabledBack, view]);
    const selection = useMemo(() => {
        if (view === 'day') {
            return (React.createElement(React.Fragment, null,
                React.createElement(Square, { px: "16px", onClick: () => setView('month') }, date.setLocale(locale).monthLong),
                React.createElement(Square, { px: "16px", onClick: () => setView('year') }, date.year)));
        }
        if (view === 'month') {
            return (React.createElement(Square, { px: "16px", onClick: () => setView('year') }, date.year));
        }
        const range = {
            start: date.year - (date.year % 10) - 1,
            end: date.year - (date.year % 10) + 10,
        };
        return (React.createElement(Square, { px: "16px", "$readOnly": true }, `${range.start} - ${range.end}`));
    }, [view, date, locale, setView]);
    return (React.createElement(HeaderBase, null,
        React.createElement(Box, null, selection),
        React.createElement(Box, null, arrows)));
}
export const HeaderBase = styled.div `
  display: flex;
  justify-content: space-between;

  padding: 8px;
  border-bottom: 1px solid var(--color-border);
`;
export default Header;
